/// useInstances: React hook for process instance lifecycle management.
/// Provides startInstance and cancelInstance operations against engine workers.

import { useCallback } from "react";
import { getWorkerManager } from "@/lib/WorkerManager";
import { useEngineStore } from "@/stores/engineStore";
import { useUIStore } from "@/stores/uiStore";
import type { StartInstanceResponse } from "@/protocol";

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Hook for managing process instances:
 * - Start a new BPMN instance with optional initial variables
 * - Cancel a running instance
 * - Inspect instance state
 */
export function useInstances() {
  /**
   * Start a new process instance from a deployed process definition.
   * @param processId - The BPMN process ID from the deployed definition
   * @param variables - Optional initial variables as a Record
   * @returns The instance key of the newly created instance
   */
  const startInstance = useCallback(
    async (processId: string, variables?: Record<string, unknown>): Promise<string> => {
      const manager = getWorkerManager();

      // Find the BPMN engine worker
      const engineWorkerId = manager.getWorkerIdByRole("bpmn-engine");
      if (!engineWorkerId) {
        throw new Error("No BPMN engine worker is running");
      }

      // Build payload -- process_key matches C++ FlowyWasmBridge handleBPMNStart
      const payload: Record<string, unknown> = {
        process_key: processId,
      };
      if (variables && Object.keys(variables).length > 0) {
        payload.variables = JSON.stringify(variables);
      }

      const result = await manager.sendCommand(
        engineWorkerId,
        {
          type: "start",
          direction: "cmd" as const,
          payload,
        } as never,
        15000,
      );

      const respPayload = result.payload as StartInstanceResponse["payload"];
      if (respPayload.error) {
        throw new Error(respPayload.error);
      }

      const instanceKey = respPayload.instance_key;
      if (!instanceKey) {
        throw new Error("Engine did not return an instance key");
      }

      // The instance will be added to engineStore via the instanceCreated event
      // from the engine worker. We just notify here.
      useUIStore.getState().addNotification({
        type: "success",
        message: `Started instance ${instanceKey.slice(0, 8)}... for "${processId}"`,
        duration: 4000,
      });

      return instanceKey;
    },
    [],
  );

  /**
   * Start a node flow instance.
   * @param flowId - The node flow ID from the deployed definition
   * @param variables - Optional initial variables
   * @returns The instance key
   */
  const startNodeFlowInstance = useCallback(
    async (flowId: string, variables?: Record<string, unknown>): Promise<string> => {
      const manager = getWorkerManager();

      const engineWorkerId = manager.getWorkerIdByRole("node-engine");
      if (!engineWorkerId) {
        throw new Error("No Node engine worker is running");
      }

      const payload: Record<string, unknown> = {
        flow_key: flowId, // C++ FlowyWasmBridge handleNodeExecute expects flow_key
      };
      if (variables && Object.keys(variables).length > 0) {
        payload.variables = JSON.stringify(variables);
      }

      const result = await manager.sendCommand(
        engineWorkerId,
        {
          type: "execute",
          direction: "cmd" as const,
          payload,
        } as never,
        15000,
      );

      const respPayload = result.payload as StartInstanceResponse["payload"];
      if (respPayload.error) {
        throw new Error(respPayload.error);
      }

      const instanceKey = respPayload.instance_key;
      if (!instanceKey) {
        throw new Error("Engine did not return an instance key");
      }

      useUIStore.getState().addNotification({
        type: "success",
        message: `Started node flow instance ${instanceKey.slice(0, 8)}...`,
        duration: 4000,
      });

      return instanceKey;
    },
    [],
  );

  /**
   * Cancel a running instance (best-effort).
   * @param instanceKey - The instance to cancel
   */
  const cancelInstance = useCallback(async (instanceKey: string) => {
    // Update local state immediately
    useEngineStore.getState().updateInstanceState(instanceKey, "suspended");

    useUIStore.getState().addNotification({
      type: "info",
      message: `Cancelled instance ${instanceKey.slice(0, 8)}...`,
      duration: 3000,
    });
  }, []);

  return { startInstance, startNodeFlowInstance, cancelInstance };
}
